<script>
import Title from "@/components/admin/common/TitleArea.vue"
import CandidateList from "@/components/fulfillment/CandidateList.vue"
import StatusBadge from "@/components/fulfillment/StatusBadge.vue"
import PageGuideDialog from "@/components/admin/fulfillment/guide/PageGuideDialog.vue"
import FulfillmentPageGuide from "@/components/admin/fulfillment/guide/FulfillmentPageGuide.vue"

export default {
  name: 'AdminSkuRequestDetail',
  components: {
    Title,
    CandidateList,
    StatusBadge,
    PageGuideDialog,
    FulfillmentPageGuide
  },
  data() {
    return {
      description: {
        title: '승인 대기 상세'
      },
      requestId: null,
      requestDetail: null,
      candidates: [],
      selectedCandidateId: null,
      loading: false,
      candidateLoading: false,
      approveLoading: false,
      rejectLoading: false,
      guideDialog: false,
      rejectReason: ''
    }
  },
  computed: {
    isPending() {
      return this.requestDetail?.status === 'REQUESTED'
    },
    isLinkRequest() {
      return this.requestDetail?.request_type === 'LINK'
    },
    requestTypeLabel() {
      return this.isLinkRequest ? '연결요청' : '신규등록요청'
    },
    requestGuideText() {
      return this.isLinkRequest
        ? '동일 조건의 기준 상품이 있습니다. 기존 상품 연결 승인을 권장합니다.'
        : '동일 조건의 기준 상품이 없습니다. 신규 상품 생성을 우선 검토하세요.'
    },
    recommendedActionTitle() {
      return this.isLinkRequest ? '기존 상품 연결 승인' : '신규 상품 생성 후 승인'
    },
    alternativeActionTitle() {
      return this.isLinkRequest ? '신규 상품으로 등록' : '기존 상품에 연결'
    },
  },
  methods: {
    goToList() {
      let query = { tab: 'requested' }
      try {
        query = JSON.parse(String(this.$route.query?.returnQuery || '{"tab":"requested"}'))
      } catch (_) {
        query = { tab: 'requested' }
      }
      this.$router.push({ path: '/admin/fulfillment/request-mappings', query })
    },
    notify(message, type = 'negative') {
      if (this.$q && this.$q.notify) {
        this.$q.notify({ message, type })
        return
      }
      alert(message)
    },
    async loadDetail() {
      if (!this.requestId) return
      this.loading = true
      const config = {
        method: 'GET',
        url: `/api/fulfillment/admin/sku-requests/${this.requestId}`,
        name: '[ Fulfillment / Admin 승인 상세 ]'
      }
      await this.$http(config)
        .then(res => { this.requestDetail = res.data?.data })
        .catch(() => { this.requestDetail = null })
      this.loading = false
    },
    async loadCandidates() {
      if (!this.requestDetail) return
      const spec_key = this.requestDetail.spec_key || this.$makeSpecKey(this.requestDetail.spec_type, this.requestDetail.spec_detail || {})
      if (!spec_key) return
      this.candidateLoading = true
      const config = {
        method: 'POST',
        url: '/api/fulfillment/master-skus/candidates',
        data: {
          brand_id: this.requestDetail.brand_id,
          category_id: this.requestDetail.category_id,
          hs_code: this.requestDetail.hs_code,
          spec_type: this.requestDetail.spec_type,
          spec_key
        },
        name: '[ Fulfillment / Master SKU 후보 조회 ]'
      }
      await this.$http(config)
        .then(res => {
          this.candidates = res.data?.data || []
          if (!this.selectedCandidateId && this.requestDetail?.resolved_master_sku_id) {
            this.selectedCandidateId = this.requestDetail.resolved_master_sku_id
          }
        })
        .catch(() => { this.candidates = [] })
      this.candidateLoading = false
    },
    onCandidateSelect(masterSkuId) {
      this.selectedCandidateId = masterSkuId
    },
    goToSelectedProduct() {
      if (!this.selectedCandidateId) return
      this.$router.push(`/admin/fulfillment/master-skus/${this.selectedCandidateId}`)
    },
    async approveLink() {
      if (!this.selectedCandidateId) {
        this.notify('후보를 선택하세요.')
        return
      }
      this.approveLoading = true
      const config = {
        method: 'POST',
        url: `/api/fulfillment/admin/sku-requests/${this.requestId}/approve/link`,
        data: { master_sku_id: this.selectedCandidateId },
        name: '[ Fulfillment / 승인(A) ]'
      }
      await this.$http(config)
        .then(() => {
          this.notify('승인이 완료되었습니다.', 'positive')
          this.loadDetail()
        })
        .catch(() => {
          this.notify('승인에 실패했습니다.')
        })
      this.approveLoading = false
    },
    async approveCreateMaster() {
      this.approveLoading = true
      const config = {
        method: 'POST',
        url: `/api/fulfillment/admin/sku-requests/${this.requestId}/approve/create-master`,
        name: '[ Fulfillment / 승인(B) ]'
      }
      await this.$http(config)
        .then(() => {
          this.notify('승인이 완료되었습니다.', 'positive')
          this.loadDetail()
        })
        .catch(() => {
          this.notify('승인에 실패했습니다.')
        })
      this.approveLoading = false
    },
    async rejectRequest() {
      if (!this.rejectReason) {
        this.notify('거절 사유를 입력하세요.')
        return
      }
      this.rejectLoading = true
      const config = {
        method: 'POST',
        url: `/api/fulfillment/admin/sku-requests/${this.requestId}/reject`,
        data: { reject_reason: this.rejectReason },
        name: '[ Fulfillment / 거절 ]'
      }
      await this.$http(config)
        .then(() => {
          this.notify('거절 처리되었습니다.', 'positive')
          this.loadDetail()
        })
        .catch(() => {
          this.notify('거절 처리에 실패했습니다.')
        })
      this.rejectLoading = false
    }
  },
  async mounted() {
    this.requestId = this.$route.params.id
    await this.loadDetail()
    await this.loadCandidates()
  }
}
</script>

<template>
  <q-page class="q-pa-lg">
    <div class="row q-mb-lg">
      <div class="col">
        <Title :desc="description">
          <template #title-action>
            <q-btn flat round dense size="sm" color="primary" icon="help_outline" aria-label="요청 상세 사용 가이드" @click="guideDialog = true">
              <q-tooltip>요청 상세 사용 가이드</q-tooltip>
            </q-btn>
          </template>
          <q-btn flat color="grey-7" icon="arrow_back" label="목록으로" @click="goToList" />
        </Title>
      </div>
    </div>

    <div class="row q-mb-md">
      <div class="col">
        <div class="comp-box q-pa-md">
          <div class="row items-center q-mb-sm">
            <div class="text-subtitle1">요청 상품 정보</div>
            <div class="q-ml-sm">
              <StatusBadge :status="requestDetail?.status" />
            </div>
          </div>
          <div v-if="requestDetail" class="row q-col-gutter-md q-mb-md">
            <div class="col-12 col-md-4">
              <div class="text-caption text-grey">요청 ID</div>
              <div class="text-body2">{{ requestDetail.request_id }}</div>
            </div>
            <div class="col-12 col-md-4">
              <div class="text-caption text-grey">파트너</div>
              <div class="text-body2">{{ requestDetail.partner_name || `#${requestDetail.partner_id}` }}</div>
            </div>
            <div class="col-12 col-md-4">
              <div class="text-caption text-grey">파트너 SKU</div>
              <div class="text-body2">{{ requestDetail.partner_sku_code }}</div>
            </div>
            <div class="col-12 col-md-4">
              <div class="text-caption text-grey">바코드번호</div>
              <div class="text-body2">{{ requestDetail.barcode || '-' }}</div>
            </div>
            <div class="col-12 col-md-4">
              <div class="text-caption text-grey">브랜드/카테고리</div>
              <div class="text-body2">{{ `${requestDetail.brand_name || ''} / ${requestDetail.category_name || ''}` }}</div>
            </div>
            <div class="col-12 col-md-4">
              <div class="text-caption text-grey">HS Code</div>
              <div class="text-body2">{{ requestDetail.hs_code }}</div>
            </div>
            <div class="col-12 col-md-4">
              <div class="text-caption text-grey">규격 타입</div>
              <div class="text-body2">{{ requestDetail.spec_type }}</div>
            </div>
            <div class="col-12 col-md-4">
              <div class="text-caption text-grey">요청 유형</div>
              <div class="text-body2">{{ requestTypeLabel }}</div>
            </div>
            <div class="col-12 col-md-4">
              <div class="text-caption text-grey">상품 식별키</div>
              <div class="text-body2">{{ requestDetail.spec_key }}</div>
            </div>
            <div v-if="requestDetail.reject_reason" class="col-12">
              <div class="text-caption text-grey">거절 사유</div>
              <div class="text-body2 text-negative">{{ requestDetail.reject_reason }}</div>
            </div>
          </div>
          <div v-else class="text-caption text-grey">요청 정보를 불러오는 중...</div>
        </div>
      </div>
    </div>

    <div class="row q-mb-md">
      <div class="col">
        <div class="comp-box q-pa-md">
          <div class="row items-center q-mb-sm">
            <div class="text-subtitle1">기존 상품 후보</div>
            <q-btn
              flat
              dense
              label="다시 불러오기"
              class="q-ml-sm"
              :loading="candidateLoading"
              @click="loadCandidates"
            />
          </div>
          <CandidateList
            :candidates="candidates"
            @select="onCandidateSelect"
          />
          <div v-if="selectedCandidateId" class="row items-center q-gutter-sm text-caption text-grey q-mt-sm">
            <span>선택된 상품 ID: {{ selectedCandidateId }}</span>
            <q-btn
              flat
              dense
              color="primary"
              label="상품 상세 보기"
              @click="goToSelectedProduct"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="col">
        <div class="comp-box q-pa-md">
          <div class="text-subtitle1 q-mb-md">처리 액션</div>

          <q-banner
            v-if="requestDetail"
            rounded
            :class="['action-guide q-mb-md', isLinkRequest ? 'action-guide--link' : 'action-guide--create']"
          >
            <div class="row items-center no-wrap">
              <q-icon :name="isLinkRequest ? 'link' : 'add_box'" color="primary" size="24px" class="q-mr-sm" />
              <div>
                <div class="text-weight-medium">{{ requestTypeLabel }}</div>
                <div class="text-caption q-mt-xs">{{ requestGuideText }}</div>
              </div>
            </div>
          </q-banner>

          <div v-if="isPending" class="row q-col-gutter-md items-stretch">
            <div class="col-12 col-lg-4">
              <div class="action-card action-card--recommended q-pa-md">
                <div class="action-card-label">권장 처리</div>
                <div class="text-subtitle1 q-mt-sm">{{ recommendedActionTitle }}</div>
                <div class="text-caption text-grey-7 q-mt-xs">
                  {{ isLinkRequest
                    ? '선택한 기존 상품과 파트너 SKU를 연결합니다.'
                    : '요청 정보로 새 기준 상품을 생성하고 연결합니다.' }}
                </div>
                <q-btn
                  v-if="isLinkRequest"
                  color="positive"
                  icon="link"
                  label="기존 상품 연결 승인"
                  :disable="!selectedCandidateId"
                  :loading="approveLoading"
                  @click="approveLink"
                  class="full-width q-mt-md"
                />
                <q-btn
                  v-else
                  color="primary"
                  icon="add_box"
                  label="신규 상품 생성 후 승인"
                  :loading="approveLoading"
                  @click="approveCreateMaster"
                  class="full-width q-mt-md"
                />
                <div v-if="isLinkRequest && !selectedCandidateId" class="text-caption text-negative q-mt-sm">
                  기존 상품 후보를 먼저 선택하세요.
                </div>
              </div>
            </div>

            <div class="col-12 col-lg-4">
              <div class="action-card q-pa-md">
                <div class="action-card-label">다른 방식으로 처리</div>
                <div class="text-subtitle1 q-mt-sm">{{ alternativeActionTitle }}</div>
                <div class="text-caption text-grey-7 q-mt-xs">
                  {{ isLinkRequest
                    ? '기존 후보가 적절하지 않은 예외 상황에서 사용합니다.'
                    : '적절한 기존 상품 후보가 있을 때 연결 방식으로 처리합니다.' }}
                </div>
                <q-btn
                  v-if="isLinkRequest"
                  outline
                  color="primary"
                  icon="add_box"
                  label="신규 상품으로 등록"
                  :loading="approveLoading"
                  @click="approveCreateMaster"
                  class="full-width q-mt-md"
                />
                <q-btn
                  v-else
                  outline
                  color="positive"
                  icon="link"
                  label="기존 상품에 연결"
                  :disable="!selectedCandidateId"
                  :loading="approveLoading"
                  @click="approveLink"
                  class="full-width q-mt-md"
                />
                <div v-if="!isLinkRequest && !selectedCandidateId" class="text-caption text-grey-7 q-mt-sm">
                  연결하려면 기존 상품 후보를 먼저 선택하세요.
                </div>
              </div>
            </div>

            <div class="col-12 col-lg-4">
              <div class="action-card q-pa-md">
                <div class="action-card-label">반려 처리</div>
                <div class="text-subtitle1 q-mt-sm">요청 반려</div>
                <div class="text-caption text-grey-7 q-mt-xs q-mb-md">
                  보완이 필요한 내용을 파트너에게 전달합니다.
                </div>
                <q-input
                  v-model="rejectReason"
                  label="반려 사유"
                  type="textarea"
                  autogrow
                  dense
                  outlined
                />
                <q-btn
                  outline
                  color="negative"
                  icon="block"
                  label="요청 반려"
                  :loading="rejectLoading"
                  @click="rejectRequest"
                  class="full-width q-mt-md"
                />
              </div>
            </div>
          </div>

          <div v-else class="processed-state q-pa-md">
            <q-icon name="check_circle" color="positive" size="24px" class="q-mr-sm" />
            <div>
              <div class="text-weight-medium">이미 처리된 요청입니다.</div>
              <div class="text-caption text-grey-7 q-mt-xs">
                현재 상태는 {{ requestDetail?.status || '-' }} 입니다.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PageGuideDialog
      v-model="guideDialog"
      title="요청 상세 사용 가이드"
      description="파트너 요청 확인과 승인 또는 반려 처리 방법입니다."
    >
      <FulfillmentPageGuide guide-key="requestDetail" />
    </PageGuideDialog>
  </q-page>
</template>

<style scoped>
.action-guide {
  border: 1px solid #d8e3ef;
}

.action-guide--link {
  background: #eef7ff;
}

.action-guide--create {
  background: #f3f8ff;
}

.action-card {
  height: 100%;
  border: 1px solid #e1e6ed;
  border-radius: 10px;
  background: #fff;
}

.action-card--recommended {
  border-color: #90caf9;
  background: #f8fbff;
}

.action-card-label {
  color: #607d8b;
  font-size: 12px;
  font-weight: 600;
}

.processed-state {
  display: flex;
  align-items: center;
  border: 1px solid #c8e6c9;
  border-radius: 10px;
  background: #f1f8f2;
}
</style>
